import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { ChevronDown, ChevronRight, FileEarmarkPdfFill } from 'react-bootstrap-icons';
import { Check, ClipboardCheck, Copy, Eye, Pencil, RefreshCw, Sparkles, ThumbsDown, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import AlertModal from '../AlertModal';
import RewriteQaRulesModal from '../BackOffice/RewriteQaRulesModal';
import { copyToClipboard } from '../../helpers/global';
import { toSafeApiErrorHtml } from '../../helpers/safeHtml';
import CircularProgressBar from '../circular-progress';
import InlineEditSaveCancel from '../services/pipeline/InlineEditSaveCancel';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS, ORDER_TYPES } from '../../helpers/constants';
import {
  analyzeReportQaIssues,
  buildSectionsFromQaDocument,
  createEmptySections,
  formatQaFieldLabel,
  getQaSummary,
  getValidateSupportingFilesEndpoint,
  isCoordinatorManualIssue,
  resolveReportOrderId,
  saveReportQaIssues
} from './utils/helpers';
import { FIELD_TONE_STYLES } from '../common/fieldStatus';

const QA_ISSUE_STATUS_BADGE = {
  Mismatch: FIELD_TONE_STYLES.error,
  'Missing Doc': FIELD_TONE_STYLES.warning,
  'Missing Value': FIELD_TONE_STYLES.warning,
  Skipped: FIELD_TONE_STYLES.default,
  Manual: FIELD_TONE_STYLES.default
};

const ESTOPPEL_HOA_QA_SECTIONS = new Set(['hoa1', 'hoa2', 'hoa3']);

const QA_SEVERITY_BADGE = {
  Critical: { color: '#991b1b', backgroundColor: '#fee2e2' },
  Warning: { color: '#92400e', backgroundColor: '#fef3c7' },
  'Suggested Info': { color: '#1e3a5f', backgroundColor: '#e0e7ff' }
};

const getDriveFileViewUrl = fileId => (fileId ? `https://drive.google.com/file/d/${fileId}/view` : null);

const getQaSourceFileDisplayName = file => file?.fileName || file?.name || 'Source PDF';

const QA_FIELD_CARD_STYLE = {
  border: '1px solid #e8ecf0',
  borderRadius: 6,
  padding: '6px 8px',
  backgroundColor: '#fafbfc'
};

const QA_FIELD_VALUE_ROW_STYLE = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '0.35rem',
  width: '100%',
  border: 'none',
  background: 'transparent',
  padding: '2px 0',
  textAlign: 'left',
  fontSize: '0.75rem',
  lineHeight: 1.4
};

const copyQaFieldValue = async value => {
  const text = String(value ?? '').trim();
  if (!text || text === '—') return;
  await copyToClipboard(text);
  toast.success(`"${text}" copied`);
};

const QaFieldValueRow = ({ icon: Icon, iconColor, valueLabel, value, isResolved, valueColor }) => {
  const displayValue = value || '—';
  const canCopy = displayValue !== '—';

  return (
    <button
      type="button"
      className={`qa-field-value-row${canCopy ? ' qa-field-value-row--copyable' : ''}`}
      style={{
        ...QA_FIELD_VALUE_ROW_STYLE,
        color: isResolved ? '#b8c0cc' : '#5f6b7a',
        cursor: canCopy ? 'pointer' : 'default'
      }}
      disabled={!canCopy}
      title={canCopy ? `Copy ${valueLabel.toLowerCase()}` : undefined}
      onMouseEnter={e => {
        if (canCopy) e.currentTarget.style.backgroundColor = '#f1f5f9';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.backgroundColor = 'transparent';
      }}
      onClick={() => void copyQaFieldValue(displayValue)}
    >
      <Icon
        size={13}
        strokeWidth={2.5}
        color={isResolved ? '#c5ccd6' : iconColor}
        style={{ flexShrink: 0, marginTop: 1 }}
      />
      <span style={{ flexShrink: 0, minWidth: 52, color: isResolved ? '#c5ccd6' : '#94a3b8' }}>
        {valueLabel}
      </span>
      <span
        className="font-weight-medium"
        style={{
          flex: 1,
          color: isResolved ? '#c5ccd6' : valueColor || '#334155',
          wordBreak: 'break-word'
        }}
      >
        {displayValue}
      </span>
      {canCopy ? (
        <Copy
          size={11}
          strokeWidth={2}
          color={isResolved ? '#c5ccd6' : '#94a3b8'}
          style={{ flexShrink: 0, marginTop: 2, opacity: 0.7 }}
          aria-hidden
        />
      ) : null}
    </button>
  );
};

const QaIssueFieldDetails = ({ issue }) => {
  const issueType = issue.raw?.issue;
  const fieldDetails = issue.fieldDetails || [];
  const isResolved = !!issue.isResolved;

  if (!fieldDetails.length || issue.isManual || issueType === 'missing source') {
    return null;
  }

  const hasStructuredDetails = fieldDetails.some(detail => {
    if (issueType === 'mismatch') {
      return !!(detail.expectedValue || detail.actualValue);
    }
    if (issueType === 'missing entry') {
      return !!detail.expectedValue;
    }
    if (issueType === 'skipped') {
      return !!detail.skipReason;
    }
    return false;
  });

  if (!hasStructuredDetails) {
    return null;
  }

  const titleColor = isResolved ? '#c5ccd6' : '#334155';

  return (
    <div className="mt-2 d-flex flex-column" style={{ gap: '0.35rem' }}>
      {fieldDetails.map(detail => {
        const fieldLabel = formatQaFieldLabel(detail.fieldName);

        return (
          <div
            key={detail.fieldName}
            style={{
              ...QA_FIELD_CARD_STYLE,
              backgroundColor: isResolved ? '#f4f5f7' : QA_FIELD_CARD_STYLE.backgroundColor,
              borderColor: isResolved ? '#eceff3' : QA_FIELD_CARD_STYLE.border
            }}
          >
            <div
              className="font-weight-medium"
              style={{ fontSize: '0.72rem', color: titleColor, marginBottom: 4, letterSpacing: '0.01em' }}
            >
              {fieldLabel}
            </div>

            {issueType === 'mismatch' ? (
              <QaFieldValueRow
                icon={Check}
                iconColor="#16a34a"
                valueLabel="Expected"
                value={detail.expectedValue}
                isResolved={isResolved}
                valueColor="#15803d"
              />
            ) : null}

            {issueType === 'missing entry' ? (
              <QaFieldValueRow
                icon={Check}
                iconColor="#16a34a"
                valueLabel="On document"
                value={detail.expectedValue}
                isResolved={isResolved}
                valueColor="#15803d"
              />
            ) : null}

            {issueType === 'skipped' ? (
              <QaFieldValueRow
                icon={X}
                iconColor="#94a3b8"
                valueLabel="Reason"
                value={detail.skipReason || 'Verification skipped'}
                isResolved={isResolved}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const QaIssueSourceFiles = ({ issue }) => {
  const sourceFiles = (issue.raw?.sourceFiles || []).filter(f => f?.id && (f.fileName || f.name));
  const isResolved = !!issue.isResolved;

  const handlePreviewFile = (file, e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = getDriveFileViewUrl(file.id);
    if (!url) {
      toast.error('File is not available for preview');
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (sourceFiles.length > 0) {
    return (
      <div
        className="d-flex flex-wrap align-items-start mt-1"
        style={{ gap: '6px', opacity: isResolved ? 0.65 : 1 }}
        onClick={e => e.stopPropagation()}
      >
        {sourceFiles.map((file, idx) => {
          const displayName = getQaSourceFileDisplayName(file);
          return (
            <div
              key={`${file.id}-${idx}`}
              role="button"
              tabIndex={0}
              className="workflow-instruction-file-card qa-source-file-card bg-white border d-flex align-items-center"
              style={{
                borderRadius: 8,
                minWidth: 0,
                maxWidth: 168,
                width: '100%',
                gap: 6,
                padding: '4px 6px',
                boxShadow: '0 1px 2px rgba(0,0,0,0.06)',
                textDecoration: isResolved ? 'line-through' : undefined,
                cursor: 'pointer'
              }}
              title={`Preview ${displayName}`}
              onClick={e => handlePreviewFile(file, e)}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePreviewFile(file, e);
                }
              }}
            >
              <div
                className="d-flex align-items-center justify-content-center flex-shrink-0"
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 5,
                  backgroundColor: '#ffe9ea'
                }}
              >
                <FileEarmarkPdfFill size={11} className="text-danger" />
              </div>
              <div
                className="qa-source-file-card__name text-left font-weight-bold text-truncate"
                style={{ fontSize: '0.68rem', lineHeight: 1.2 }}
              >
                {displayName}
              </div>
              <div className="workflow-instruction-file-card__actions d-flex align-items-center flex-shrink-0">
                <span
                  className="p-0 rounded-circle border d-flex align-items-center justify-content-center bg-light"
                  style={{ minWidth: 20, minHeight: 20, width: 20, height: 20 }}
                  aria-hidden="true"
                >
                  <Eye size={11} className="text-dark" />
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (!issue.file) return null;

  return (
    <small
      className={`d-flex align-items-center gap-1 mt-1${isResolved ? '' : ' text-muted'}`}
      style={{
        fontSize: '0.85rem',
        wordBreak: 'break-word',
        color: isResolved ? '#b8c0cc' : undefined,
        textDecoration: isResolved ? 'line-through' : undefined
      }}
    >
      <span>📎</span>
      {issue.file}
    </small>
  );
};

// Viewport height minus sliding panel header, top card, and step tabs.
const QA_PANEL_HEIGHT = 'calc(100vh - 14rem)';

const updateSectionIssue = (prevSections, sectionKey, issueId, updater) => {
  const sectionIndex = prevSections.findIndex(s => s.key === sectionKey);
  if (sectionIndex === -1) return prevSections;

  const nextIssues = prevSections[sectionIndex].issues.map(item => (item.id === issueId ? updater(item) : item));
  const resolvedCount = nextIssues.filter(i => i.isResolved).length;
  const nextSections = [...prevSections];
  nextSections[sectionIndex] = {
    ...nextSections[sectionIndex],
    issues: nextIssues,
    count: `${resolvedCount}/${nextIssues.length}`
  };
  return nextSections;
};

const removeSectionIssue = (prevSections, sectionKey, issueId) => {
  const sectionIndex = prevSections.findIndex(s => s.key === sectionKey);
  if (sectionIndex === -1) return prevSections;

  const nextIssues = prevSections[sectionIndex].issues.filter(item => item.id !== issueId);
  const resolvedCount = nextIssues.filter(i => i.isResolved).length;
  const nextSections = [...prevSections];
  nextSections[sectionIndex] = {
    ...nextSections[sectionIndex],
    issues: nextIssues,
    count: `${resolvedCount}/${nextIssues.length}`
  };
  return nextSections;
};

const QaAnalysisSideBar = ({
  onClose,
  orderId: orderIdProp,
  qaDocumentId: qaDocumentIdProp,
  onQaDocumentIdChange,
  sections: sectionsProp,
  onIssueClick,
  onIssuesChange,
  activeIssueId,
  hasUnsavedChanges = false,
  onSaveReport,
  isSavingReport = false,
  orderType = ORDER_TYPES.LIEN_SEARCH,
  productLabel,
  analyzedAt = null
}) => {
  const orderId = useMemo(() => resolveReportOrderId(orderIdProp), [orderIdProp]);
  const resolvedProductLabel =
    productLabel || (orderType === ORDER_TYPES.ESTOPPEL ? 'Estoppel' : 'Lien Search');
  const isEstoppel = orderType === ORDER_TYPES.ESTOPPEL;
  const [qaDocumentId, setQaDocumentId] = useState(qaDocumentIdProp || null);
  const [newIssue, setNewIssue] = useState('');
  const [sections, setSections] = useState(() => sectionsProp || createEmptySections(orderType));
  const [isSaving, setIsSaving] = useState(false);
  const [isVerifyingOrder, setIsVerifyingOrder] = useState(false);
  const [missingFilesModalMeta, setMissingFilesModalMeta] = useState(null);
  const [showUnsavedChangesAlert, setShowUnsavedChangesAlert] = useState(false);
  const [resolveConfirmMeta, setResolveConfirmMeta] = useState(null);
  const [editingIssueMeta, setEditingIssueMeta] = useState(null);
  const [deleteConfirmMeta, setDeleteConfirmMeta] = useState(null);
  const [showRerunAnalysisAlert, setShowRerunAnalysisAlert] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});
  const [qaAnalyzedAt, setQaAnalyzedAt] = useState(analyzedAt);
  const [feedbackMeta, setFeedbackMeta] = useState(null);
  const [rewritingFeedback, setRewritingFeedback] = useState(false);

  useEffect(() => {
    if (sectionsProp) setSections(sectionsProp);
  }, [sectionsProp]);

  useEffect(() => {
    setExpandedSections(prev => {
      const next = { ...prev };
      let changed = false;
      sections.forEach(section => {
        if (!(section.key in next)) {
          next[section.key] = false;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [sections]);

  const toggleSection = useCallback(sectionKey => {
    setExpandedSections(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
  }, []);

  useEffect(() => {
    setQaDocumentId(qaDocumentIdProp || null);
  }, [qaDocumentIdProp]);

  useEffect(() => {
    setQaAnalyzedAt(analyzedAt);
  }, [analyzedAt]);

  const applyQaDocument = useCallback(
    qaDocument => {
      const nextSections = buildSectionsFromQaDocument(qaDocument, orderType);
      setSections(nextSections);
      onIssuesChange?.(nextSections);
      if (qaDocument?.lastAnalyzedAt || qaDocument?.updatedAt) {
        setQaAnalyzedAt(qaDocument.lastAnalyzedAt || qaDocument.updatedAt);
      }
      if (qaDocument?._id) {
        setQaDocumentId(qaDocument._id);
        onQaDocumentIdChange?.(qaDocument._id);
      }
    },
    [onIssuesChange, onQaDocumentIdChange, orderType]
  );

  const persistSections = useCallback(
    async nextSections => {
      setIsSaving(true);
      const { response, error } = await saveReportQaIssues({
        qaDocumentId,
        orderId,
        sections: nextSections,
        orderType
      });
      setIsSaving(false);

      if (error) {
        toast.error(error);
        return false;
      }

      if (response) {
        applyQaDocument(response);
      } else {
        setSections(nextSections);
        onIssuesChange?.(nextSections);
      }
      return true;
    },
    [qaDocumentId, orderId, applyQaDocument, onIssuesChange, orderType]
  );

  const summary = useMemo(() => getQaSummary(sections), [sections]);
  const isQaPassed = useMemo(() => {
    if (!qaDocumentId) return false;
    const actionable = sections
      .flatMap(section => section.issues || [])
      .filter(issue => !(issue.severity === 'Suggested Info' && issue.raw?.issue === 'skipped'));
    return actionable.length === 0;
  }, [qaDocumentId, sections]);

  const openResolveConfirm = (sectionKey, issue) => {
    setResolveConfirmMeta({
      show: true,
      sectionKey,
      issueId: issue.id,
      issueTitle: issue.title,
      nextResolved: !issue.isResolved
    });
  };

  const handleConfirmResolveToggle = async () => {
    const { sectionKey, issueId, nextResolved } = resolveConfirmMeta;
    setResolveConfirmMeta(null);

    const nextSections = updateSectionIssue(sections, sectionKey, issueId, issue => ({
      ...issue,
      isResolved: nextResolved
    }));

    await persistSections(nextSections);
  };

  const runOrderAnalysis = async () => {
    const loadingToastId = toast.loading('Running QA analysis…');

    try {
      const { response, error } = await analyzeReportQaIssues(orderId, orderType);
      if (error) {
        toast.error('', { description: <div dangerouslySetInnerHTML={{ __html: toSafeApiErrorHtml(error) }} /> });
        return;
      }

      if (!response) {
        toast.error('QA analysis completed but no results were returned.');
        return;
      }

      applyQaDocument(response);

      if (Array.isArray(response.skippedMessages) && response.skippedMessages.length) {
        response.skippedMessages.forEach(message => toast.info(message));
      }

      const aiCount = (response?.issues || []).filter(issue => !isCoordinatorManualIssue(issue)).length;
      toast.success(
        aiCount ? `Analysis complete — ${aiCount} QA issue(s) found.` : 'Analysis complete — no QA issues found.'
      );
    } finally {
      toast.dismiss(loadingToastId);
    }
  };

  const handleVerifyOrder = async ({ acknowledgeMissingFiles = false, skipUnsavedCheck = false } = {}) => {
    if (!orderId) {
      toast.error('Order ID required — save the report first.');
      return;
    }

    if (!skipUnsavedCheck && hasUnsavedChanges) {
      setShowUnsavedChangesAlert(true);
      return;
    }

    setIsVerifyingOrder(true);

    try {
      const requestBody = { orderId };

      if (acknowledgeMissingFiles || isEstoppel) {
        const { error: reportError } = await makeRESTApiRequest({
          endpoint: ENDPOINTS.VALIDATE_REPORT,
          method: 'POST',
          requestBody
        });

        if (reportError) {
          toast.error('', { description: <div dangerouslySetInnerHTML={{ __html: toSafeApiErrorHtml(reportError) }} /> });
          return;
        }
      } else {
        const [reportResult, supportingFilesResult] = await Promise.all([
          makeRESTApiRequest({
            endpoint: ENDPOINTS.VALIDATE_REPORT,
            method: 'POST',
            requestBody
          }),
          makeRESTApiRequest({
            endpoint: getValidateSupportingFilesEndpoint(orderType),
            method: 'POST',
            requestBody
          })
        ]);

        if (reportResult.error) {
          toast.error('', { description: <div dangerouslySetInnerHTML={{ __html: toSafeApiErrorHtml(reportResult.error) }} /> });
          return;
        }

        if (supportingFilesResult.error) {
          toast.error('', { description: <div dangerouslySetInnerHTML={{ __html: supportingFilesResult.error }} /> });
          return;
        }

        if (!supportingFilesResult.response?.valid) {
          setMissingFilesModalMeta({
            show: true,
            errorMessage: supportingFilesResult.response.message
          });
          return;
        }
      }

      await runOrderAnalysis();
    } finally {
      setIsVerifyingOrder(false);
    }
  };

  const handleConfirmMissingFiles = () => {
    setMissingFilesModalMeta(null);
    void handleVerifyOrder({ acknowledgeMissingFiles: true, skipUnsavedCheck: true });
  };

  const handleSaveAndRunAnalysis = async () => {
    if (!onSaveReport) {
      toast.error('Unable to save the report before running analysis.');
      return;
    }

    const saveResult = await onSaveReport();
    if (saveResult !== true) return;

    setShowUnsavedChangesAlert(false);
    void handleVerifyOrder({ skipUnsavedCheck: true });
  };

  const handleRunWithoutSaving = () => {
    setShowUnsavedChangesAlert(false);
    void handleVerifyOrder({ skipUnsavedCheck: true });
  };

  const handleConfirmRerunAnalysis = () => {
    setShowRerunAnalysisAlert(false);
    void handleVerifyOrder();
  };

  const openEditIssue = (sectionKey, issue) => {
    setEditingIssueMeta({
      sectionKey,
      issueId: issue.id,
      title: issue.title
    });
  };

  const handleConfirmEditIssue = async () => {
    if (!editingIssueMeta) return;

    const { sectionKey, issueId, title } = editingIssueMeta;
    const trimmed = title.trim();
    if (!trimmed) {
      toast.error('Checklist item text is required.');
      return;
    }

    setEditingIssueMeta(null);

    const nextSections = updateSectionIssue(sections, sectionKey, issueId, issue => ({
      ...issue,
      title: trimmed,
      raw: {
        ...issue.raw,
        description: trimmed
      }
    }));

    const saved = await persistSections(nextSections);
    if (saved) toast.success('Checklist item updated.');
  };

  const openDeleteConfirm = (sectionKey, issue) => {
    setDeleteConfirmMeta({
      show: true,
      sectionKey,
      issueId: issue.id,
      issueTitle: issue.title
    });
  };

  const handleConfirmDeleteIssue = async () => {
    const { sectionKey, issueId } = deleteConfirmMeta;
    setDeleteConfirmMeta(null);

    const nextSections = removeSectionIssue(sections, sectionKey, issueId);
    const saved = await persistSections(nextSections);
    if (saved) toast.success('Checklist item deleted.');
  };

  const handleAddIssue = async () => {
    const title = newIssue.trim();
    if (!title || isSaving) return;

    const sectionIndex = sections.findIndex(s => s.key === 'manual');
    if (sectionIndex === -1) return;

    const manualIssues = [
      ...sections[sectionIndex].issues,
      {
        id: `manual-${Date.now()}`,
        title,
        file: '',
        status: 'Manual',
        isResolved: false,
        isManual: true,
        raw: {
          section: 'manual',
          issue: 'missing entry',
          description: title,
          isManual: true,
          isResolved: false,
          sourceFiles: []
        }
      }
    ];
    const resolvedCount = manualIssues.filter(i => i.isResolved).length;
    const nextSections = [...sections];
    nextSections[sectionIndex] = {
      ...nextSections[sectionIndex],
      issues: manualIssues,
      count: `${resolvedCount}/${manualIssues.length}`
    };

    setNewIssue('');
    const saved = await persistSections(nextSections);
    if (saved) toast.success('Checklist item added.');
  };

  const openIssueFeedback = (sectionKey, issue, sectionTitle) => {
    setFeedbackMeta({
      sectionKey,
      sectionTitle,
      issueTitle: issue.title,
      issue
    });
  };

  const handleIssueFeedback = async instruction => {
    if (!feedbackMeta?.sectionKey) return;
    const issue = feedbackMeta.issue;
    const reportType = isEstoppel ? 'Estoppel' : 'Lien Search';
    const rulesetSectionKey = isEstoppel ? 'hoa' : feedbackMeta.sectionKey;
    setRewritingFeedback(true);
    const { error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.REPORT_QA_RULESET_REWRITE(reportType, rulesetSectionKey),
      method: 'POST',
      requestBody: {
        instruction,
        persist: true,
        issueFeedback: {
          title: issue?.title,
          issue: issue?.raw?.issue,
          sourceDocumentType: issue?.raw?.sourceDocumentType,
          fieldDetails: issue?.fieldDetails || []
        }
      }
    });
    setRewritingFeedback(false);

    if (error) {
      toast.error(error);
      return;
    }

    const nextSections = updateSectionIssue(
      sections,
      feedbackMeta.sectionKey,
      feedbackMeta.issue?.id,
      issue => ({ ...issue, isDisliked: true })
    );
    await persistSections(nextSections);
    setFeedbackMeta(null);
    toast.success('QA rules updated. The next analysis will use the improved instructions and documents.');
  };

  const isBusy = isSaving || isVerifyingOrder || isSavingReport || rewritingFeedback;

  const sectionsWithIssues = useMemo(() => sections.filter(section => (section.issues?.length ?? 0) > 0), [sections]);

  return (
    <>
      <div
        className="border bg-white d-flex flex-column"
        style={{
          borderColor: '#d9dee7',
          height: QA_PANEL_HEIGHT,
          maxHeight: QA_PANEL_HEIGHT,
          overflow: 'hidden',
          animation: 'workflow-panel-slide-in 260ms ease-out',
          willChange: 'transform, opacity',
          flex: '0 0 30%',
          width: '30%',
          maxWidth: '30%',
          minWidth: 0,
          alignSelf: 'flex-start',
          position: 'sticky',
          top: 0,
          marginTop: '-1.25rem'
        }}
      >
        <div
          className="px-3 py-3 d-flex justify-content-between align-items-center flex-shrink-0"
          style={{ background: '#16223a' }}
        >
          <div className="d-flex align-items-start gap-2">
            <span className="text-white mr-2" style={{ fontSize: '15px', marginTop: '2px' }}>
              ✦
            </span>
            <div>
              <h1 className="mb-0 text-white fw-bold" style={{ fontSize: '1rem', lineHeight: 1 }}>
                QA Analysis Report
              </h1>
              <p className="mb-0 text-white-50" style={{ fontSize: '0.8rem' }}>
                {resolvedProductLabel}
              </p>
            </div>
          </div>
          <button type="button" className="btn btn-sm text-white p-0 border-0 bg-transparent" onClick={onClose}>
            <span style={{ fontSize: '1.6rem', lineHeight: 1 }}>×</span>
          </button>
        </div>

        <div className="px-3 py-2 border-bottom flex-shrink-0">
          {!orderId ? (
            <small className="text-danger d-block" style={{ fontSize: '0.75rem' }}>
              Order ID required — save the report first.
            </small>
          ) : null}
          {isSaving ? (
            <small className="text-muted d-block mt-1" style={{ fontSize: '0.75rem' }}>
              Saving changes…
            </small>
          ) : null}

          <div className="progress rounded-pill mt-2" style={{ height: '7px', background: '#edf1f7' }}>
            <div
              className="progress-bar bg-success"
              role="progressbar"
              style={{ width: `${summary.progressPercent}%` }}
            />
          </div>
          <div className="d-flex justify-content-between align-items-start mt-2">
            <small className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>
              {summary.resolved} of {summary.total} issues resolved
            </small>
            <small className="text-muted fw-semibold" style={{ fontSize: '0.8rem' }}>
              ✦ AI Reviews ({summary.aiReviewed})
            </small>
          </div>
          {qaDocumentId && isQaPassed ? (
            <div
              className="mt-2 px-2 py-2 rounded-2"
              style={{ background: '#ecfdf5', border: '1px solid #a7f3d0' }}
            >
              <small className="d-block font-weight-bold" style={{ color: '#065f46' }}>
                QA Passed
              </small>
              {qaAnalyzedAt ? (
                <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                  {new Date(qaAnalyzedAt).toLocaleString()}
                </small>
              ) : null}
            </div>
          ) : null}
          {qaDocumentId ? (
            <Button
              variant="outline-secondary"
              size="sm"
              disabled={isBusy || !orderId}
              className="mt-2 w-100 d-inline-flex align-items-center justify-content-center font-weight-semibold"
              style={{ fontSize: '0.78rem' }}
              onClick={() => setShowRerunAnalysisAlert(true)}
              title="Run QA analysis again and refresh AI-generated issues."
            >
              {isVerifyingOrder ? (
                <>
                  <CircularProgressBar size={1} className="mr-1" />
                  Rerunning analysis…
                </>
              ) : (
                <>
                  <RefreshCw size={13} className="mr-1" aria-hidden />
                  Rerun Analysis
                </>
              )}
            </Button>
          ) : null}
        </div>

        <div className="flex-grow-1" style={{ minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
          {!qaDocumentId ? (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 px-4 text-center">
              <ClipboardCheck size={28} className="text-muted mb-3" aria-hidden />
              <p className="mb-1 font-weight-bold text-dark">No QA analysis yet</p>
              <small className="d-block text-muted mb-4">Run AI review to find issues in your report.</small>
              <Button
                variant="outline-dark"
                size="md"
                disabled={isBusy || !orderId}
                className="d-inline-flex align-items-center font-weight-semibold shadow-sm"
                onClick={() => void handleVerifyOrder()}
                title="Validate the report and run QA analysis."
              >
                {isVerifyingOrder ? (
                  <>
                    <CircularProgressBar size={1} className="mr-1" />
                    Running analysis…
                  </>
                ) : (
                  <>
                    <Sparkles size={14} className="mr-1" aria-hidden />
                    Run AI Analysis
                  </>
                )}
              </Button>
            </div>
          ) : (
            sectionsWithIssues.map(section => {
              const isExpanded = expandedSections[section.key] || false;

              return (
                <div key={section.key} className="border-bottom">
                  <button
                    type="button"
                    className="w-100 px-3 py-2 d-flex align-items-center gap-2 border-0 bg-transparent text-left"
                    style={{
                      background: isExpanded ? '#f4f6fa' : '#fafbfc',
                      transition: 'background 0.15s ease'
                    }}
                    onClick={() => toggleSection(section.key)}
                    aria-expanded={isExpanded}
                    aria-controls={`qa-section-${section.key}`}
                  >
                    {isExpanded ? (
                      <ChevronDown size={10} className="flex-shrink-0 text-muted" aria-hidden />
                    ) : (
                      <ChevronRight size={10} className="flex-shrink-0 text-muted" aria-hidden />
                    )}
                    <small
                      className="fw-bold text-muted flex-grow-1 mb-0 ml-2"
                      style={{ letterSpacing: '0.02em', fontSize: '0.72rem' }}
                    >
                      {section.title}
                    </small>
                    <small
                      className="fw-semibold mb-0"
                      style={{
                        fontSize: '0.72rem',
                        color: section.issues.length ? '#4a5d75' : '#a8b3c2',
                        background: '#e8edf4',
                        borderRadius: '10px',
                        padding: '2px 8px'
                      }}
                    >
                      {section.count}
                    </small>
                  </button>

                  <div
                    id={`qa-section-${section.key}`}
                    style={{
                      display: 'grid',
                      gridTemplateRows: isExpanded ? '1fr' : '0fr',
                      transition: 'grid-template-rows 0.22s ease'
                    }}
                  >
                    <div style={{ overflow: 'hidden' }}>
                      {section.issues.map(issue => {
                        const statusBadgeTone = QA_ISSUE_STATUS_BADGE[issue.status] || FIELD_TONE_STYLES.default;
                        const isEditingIssue =
                          editingIssueMeta?.sectionKey === section.key && editingIssueMeta?.issueId === issue.id;
                        const issueFieldNames = issue.fieldNames || [];
                        const issueType = issue.raw?.issue;
                        const hasNavigableField = issueFieldNames.length > 0;

                        return (
                          <div
                            key={issue.id}
                            role={hasNavigableField ? 'button' : undefined}
                            tabIndex={hasNavigableField ? 0 : undefined}
                            className={`px-3 py-3 border-top d-flex justify-content-between align-items-start gap-2${
                              hasNavigableField ? ' qa-issue-row' : ''
                            }${activeIssueId === issue.id ? ' bg-light' : ''}`}
                            style={{
                              paddingLeft: '2rem',
                              cursor: hasNavigableField ? 'pointer' : 'default',
                              outline: activeIssueId === issue.id ? '2px solid #4da3ff' : undefined,
                              outlineOffset: '-2px'
                            }}
                            onClick={() => hasNavigableField && onIssueClick?.(issue, section.key)}
                            onKeyDown={e => {
                              if (hasNavigableField && (e.key === 'Enter' || e.key === ' ')) {
                                e.preventDefault();
                                onIssueClick?.(issue, section.key);
                              }
                            }}
                          >
                            <div className="d-flex align-items-start gap-2 flex-grow-1" style={{ minWidth: 0 }}>
                              <input
                                type="checkbox"
                                className="mr-2"
                                style={{
                                  marginTop: '2px',
                                  cursor: isBusy ? 'not-allowed' : 'pointer',
                                  accentColor: '#28a745'
                                }}
                                checked={!!issue.isResolved}
                                disabled={isBusy}
                                onClick={e => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  if (!isBusy) openResolveConfirm(section.key, issue);
                                }}
                                readOnly
                                aria-label={issue.isResolved ? 'Mark as unresolved' : 'Mark as resolved'}
                              />

                              <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0 }}>
                                {isEditingIssue ? (
                                  <div
                                    className="d-flex align-items-center"
                                    style={{ minWidth: 0, gap: 6 }}
                                    onClick={e => e.stopPropagation()}
                                  >
                                    <input
                                      className="form-control form-control-sm rounded-2 flex-grow-1"
                                      value={editingIssueMeta.title}
                                      disabled={isBusy}
                                      autoFocus
                                      onChange={e => setEditingIssueMeta(prev => ({ ...prev, title: e.target.value }))}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') void handleConfirmEditIssue();
                                        if (e.key === 'Escape') setEditingIssueMeta(null);
                                      }}
                                    />
                                    <InlineEditSaveCancel
                                      saving={isBusy}
                                      onSave={handleConfirmEditIssue}
                                      onCancel={() => setEditingIssueMeta(null)}
                                      saveAriaLabel="Save checklist item"
                                    />
                                  </div>
                                ) : (
                                  <>
                                    <small
                                      className={`d-block font-weight-medium ${
                                        issue.isResolved ? 'text-muted' : 'text-dark'
                                      }`}
                                      style={issue.isResolved ? { textDecoration: 'line-through' } : undefined}
                                    >
                                      {issue.title}
                                    </small>
                                    {isEstoppel && issue.description && !issue.isManual ? (
                                      <small
                                        className={issue.isResolved ? '' : 'text-muted'}
                                        style={{
                                          fontSize: '0.78rem',
                                          color: issue.isResolved ? '#b8c0cc' : undefined
                                        }}
                                      >
                                        {issue.description}
                                      </small>
                                    ) : null}
                                    <QaIssueFieldDetails issue={issue} />
                                    {issueFieldNames.length > 0 &&
                                    !issue.isManual &&
                                    issueType === 'missing source' ? (
                                      <small
                                        className={issue.isResolved ? '' : 'text-muted'}
                                        style={{
                                          fontSize: '0.78rem',
                                          color: issue.isResolved ? '#b8c0cc' : undefined
                                        }}
                                      >
                                        Affected fields:{' '}
                                        {issueFieldNames.map(formatQaFieldLabel).join(', ')}
                                      </small>
                                    ) : null}
                                    {issueFieldNames.length > 0 &&
                                    !issue.isManual &&
                                    issueType !== 'missing source' &&
                                    !issue.fieldDetails?.some(detail =>
                                      issueType === 'mismatch'
                                        ? detail.expectedValue || detail.actualValue
                                        : issueType === 'missing entry'
                                          ? detail.expectedValue
                                          : issueType === 'skipped'
                                            ? detail.skipReason
                                            : false
                                    ) ? (
                                      <small
                                        className={issue.isResolved ? '' : 'text-muted'}
                                        style={{
                                          fontSize: '0.78rem',
                                          color: issue.isResolved ? '#b8c0cc' : undefined
                                        }}
                                      >
                                        Affected fields:{' '}
                                        {issueFieldNames.map(formatQaFieldLabel).join(', ')}
                                      </small>
                                    ) : null}
                                  </>
                                )}
                                {issue.file || issue.raw?.sourceFiles?.length ? (
                                  <QaIssueSourceFiles issue={issue} />
                                ) : null}
                              </div>
                            </div>
                            <div className="d-flex flex-column align-items-end gap-1 flex-shrink-0 ml-2">
                              <span
                                className="badge badge-pill small font-weight-medium px-2 py-1"
                                style={{
                                  color: statusBadgeTone.label.color,
                                  backgroundColor: statusBadgeTone.input.backgroundColor
                                }}
                              >
                                {issue.status}
                              </span>
                              {issue.severity ? (
                                <span
                                  className="badge badge-pill small font-weight-medium px-2 py-1"
                                  style={{
                                    fontSize: '0.65rem',
                                    ...(QA_SEVERITY_BADGE[issue.severity] || QA_SEVERITY_BADGE['Suggested Info'])
                                  }}
                                >
                                  {issue.severity}
                                </span>
                              ) : null}
                              {!issue.isManual &&
                              (!isEstoppel || ESTOPPEL_HOA_QA_SECTIONS.has(section.key)) ? (
                                <button
                                  type="button"
                                  className={`btn btn-link p-0 border-0 shadow-none ${
                                    issue.isDisliked ? 'text-danger' : 'text-muted'
                                  }`}
                                  title={
                                    issue.isDisliked
                                      ? 'Feedback sent — this point was disliked'
                                      : 'This point is incorrect or irrelevant'
                                  }
                                  disabled={isBusy}
                                  onClick={e => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    openIssueFeedback(section.key, issue, section.title);
                                  }}
                                >
                                  <ThumbsDown size={13} fill={issue.isDisliked ? 'currentColor' : 'none'} />
                                </button>
                              ) : null}
                              {issue.isManual && !isEditingIssue ? (
                                <div
                                  className="d-flex align-items-center"
                                  style={{ gap: 8 }}
                                  onClick={e => e.stopPropagation()}
                                >
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 border-0 shadow-none text-muted"
                                    title="Edit"
                                    disabled={isBusy}
                                    onClick={() => openEditIssue(section.key, issue)}
                                  >
                                    <Pencil size={13} />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 border-0 shadow-none text-danger"
                                    title="Delete"
                                    disabled={isBusy}
                                    onClick={() => openDeleteConfirm(section.key, issue)}
                                  >
                                    <Trash2 size={13} />
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
        {qaDocumentId && (
          <div className="px-3 py-3 border-top bg-light flex-shrink-0">
            <small className="text-muted fw-semibold">Add your own checklist item</small>
            <div className="d-flex gap-2 mt-1">
              <input
                className="form-control form-control-sm rounded-3"
                placeholder="e.g. Re-verify property address"
                value={newIssue}
                disabled={isBusy || !orderId}
                onChange={e => setNewIssue(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter') void handleAddIssue();
                }}
              />
              <Button
                variant="outline-success"
                className="fw-bold px-2 ml-1 rounded-2"
                size="sm"
                disabled={!newIssue.trim() || isBusy || !orderId}
                onClick={() => void handleAddIssue()}
              >
                Add
              </Button>
            </div>
          </div>
        )}
      </div>

      <AlertModal
        show={showRerunAnalysisAlert}
        onHide={() => setShowRerunAnalysisAlert(false)}
        onDismissClick={() => setShowRerunAnalysisAlert(false)}
        onContinueClick={handleConfirmRerunAnalysis}
        alertText="This will replace all automated QA results and clear their resolution status. A fresh analysis will be run against the current report. Only manual checklist items added by processors will be kept."
        continueButtonText="Yes, rerun analysis"
        dismissButtonText="Cancel"
        continueButtonVariant="warning"
        dismissButtonVariant="outline-secondary"
        showProgress={isVerifyingOrder}
        progressText="Running analysis…"
      />

      <AlertModal
        show={showUnsavedChangesAlert}
        onHide={() => setShowUnsavedChangesAlert(false)}
        onDismissClick={handleRunWithoutSaving}
        onContinueClick={() => void handleSaveAndRunAnalysis()}
        alertText="You have unsaved changes. QA analysis runs against the last saved report."
        dismissButtonText="Run Without Saving"
        dismissButtonVariant="warning"
        continueButtonText="Save & Run Analysis"
        continueButtonVariant="success"
        showProgress={isSavingReport}
        progressText="Saving..."
      />

      <AlertModal
        show={missingFilesModalMeta?.show}
        onHide={() => setMissingFilesModalMeta(null)}
        onDismissClick={() => setMissingFilesModalMeta(null)}
        onContinueClick={handleConfirmMissingFiles}
        alertText="Not enough supporting files"
        continueButtonText="Yes, continue"
        dismissButtonText="Cancel"
        continueButtonVariant="warning"
        dismissButtonVariant="outline-secondary"
        extraContent={
          missingFilesModalMeta?.errorMessage ? (
            <div
              className="text-left mt-3 small text-muted"
              dangerouslySetInnerHTML={{ __html: toSafeApiErrorHtml(missingFilesModalMeta.errorMessage) }}
            />
          ) : null
        }
      />

      <AlertModal
        show={resolveConfirmMeta?.show}
        onHide={() => setResolveConfirmMeta(null)}
        onDismissClick={() => setResolveConfirmMeta(null)}
        onContinueClick={() => void handleConfirmResolveToggle()}
        alertText={resolveConfirmMeta?.nextResolved ? 'Mark this item as resolved?' : 'Mark this item as unresolved?'}
        continueButtonText={resolveConfirmMeta?.nextResolved ? 'Yes, mark resolved' : 'Yes, mark unresolved'}
        dismissButtonText="Cancel"
        continueButtonVariant={resolveConfirmMeta?.nextResolved ? 'success' : 'warning'}
        dismissButtonVariant="outline-secondary"
        extraContent={
          resolveConfirmMeta?.issueTitle ? (
            <p className="text-muted small text-center mt-3 mb-0">{resolveConfirmMeta.issueTitle}</p>
          ) : null
        }
      />

      <AlertModal
        show={deleteConfirmMeta?.show}
        onHide={() => setDeleteConfirmMeta(null)}
        onDismissClick={() => setDeleteConfirmMeta(null)}
        onContinueClick={() => void handleConfirmDeleteIssue()}
        alertText="Delete this checklist item?"
        continueButtonText="Yes, delete"
        dismissButtonText="Cancel"
        continueButtonVariant="danger"
        dismissButtonVariant="outline-secondary"
        extraContent={
          deleteConfirmMeta?.issueTitle ? (
            <p className="text-muted small text-left mt-3 mb-0">{deleteConfirmMeta.issueTitle}</p>
          ) : null
        }
      />

      <RewriteQaRulesModal
        show={Boolean(feedbackMeta)}
        title="This QA point looks wrong"
        sectionLabel={feedbackMeta?.sectionTitle}
        helperText="We'll update this section's verification rules and documents as needed so similar false flags are less likely next time. Existing rules are kept unless they caused this mistake."
        placeholder="Why is this point irrelevant or incorrect?"
        submitLabel="Send feedback"
        submittingLabel="Updating rules…"
        rewriting={rewritingFeedback}
        requiresSave={false}
        extraContent={
          feedbackMeta?.issueTitle ? (
            <p className="bg-light rounded px-2 py-2 tinyFont mb-3 mb-2" style={{ color: '#475569' }}>
              {feedbackMeta.issueTitle}
            </p>
          ) : null
        }
        onHide={() => {
          if (!rewritingFeedback) setFeedbackMeta(null);
        }}
        onSubmit={instruction => void handleIssueFeedback(instruction)}
      />
    </>
  );
};

export default QaAnalysisSideBar;
